import { ApiError, closeEvent } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { EventResponse } from '@jq4race/shared';
import {
  Button,
  buttonVariants,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@jq4race/ui';
import { useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { useAuthState } from '../../../auth/ui/auth-state-context';

// S6 — Close event action (R6.1/R6.3/R6.4).
//
// Clicking "Close event" always attempts a plain close first. Only when the
// backend rejects with the R6.3 block (per_attendee event, unsettled ticket
// payments, no forceClose) do we surface the unsettled tickets and — ONLY
// for org_admin (R6.4, billing-sensitive override; supervisors are 403'd
// server-side) — offer a force-close confirmation. Generic failures are
// forwarded to the parent via `onError` so they render through the SAME
// inline banner already used for confirm/activate (EditEventPage).

interface CloseEventActionProps {
  eventId: string;
  onClosed: (event: EventResponse) => void;
  onError: (message: string) => void;
}

interface UnsettledBlock {
  unsettledTicketIds: string[];
}

function blockedByUnsettledPayments(error: ApiError): UnsettledBlock | null {
  if (error.status !== 409) return null;
  const details = error.details as { code?: string; unsettledTicketIds?: string[] } | null;
  if (details?.code !== 'event_close_blocked_unsettled_payments') return null;
  return { unsettledTicketIds: details.unsettledTicketIds ?? [] };
}

export function CloseEventAction({ eventId, onClosed, onError }: CloseEventActionProps) {
  const { t } = useTranslation('events');
  const { session } = useAuthState();
  const isOrgAdmin = session?.user.role === 'org_admin';

  const [closing, setClosing] = useState(false);
  const [forceClosing, setForceClosing] = useState(false);
  const [blocked, setBlocked] = useState<UnsettledBlock | null>(null);

  async function handleClose() {
    setClosing(true);
    try {
      const updated = await closeEvent(eventId);
      onClosed(updated);
    } catch (e) {
      const block = e instanceof ApiError ? blockedByUnsettledPayments(e) : null;
      if (block) {
        setBlocked(block);
      } else {
        onError(
          e instanceof ApiError
            ? translateApiError(e, t, { fallback: t('closeEventAction.closeError') })
            : t('closeEventAction.closeError'),
        );
      }
    } finally {
      setClosing(false);
    }
  }

  async function handleForceClose() {
    setForceClosing(true);
    try {
      const updated = await closeEvent(eventId, { forceClose: true });
      setBlocked(null);
      onClosed(updated);
    } catch (e) {
      onError(
        e instanceof ApiError
          ? translateApiError(e, t, { fallback: t('closeEventAction.forceCloseError') })
          : t('closeEventAction.forceCloseError'),
      );
    } finally {
      setForceClosing(false);
    }
  }

  const ticketCount = blocked?.unsettledTicketIds.length ?? 0;

  return (
    <>
      <button
        type="button"
        className={buttonVariants({ size: 'sm', variant: 'outline' })}
        onClick={() => void handleClose()}
        disabled={closing}
      >
        {closing ? t('closeEventAction.closing') : t('closeEventAction.closeEvent')}
      </button>

      <Dialog
        open={blocked !== null}
        onOpenChange={(next) => (!next ? setBlocked(null) : undefined)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('closeEventAction.outstandingDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('closeEventAction.outstandingDialog.description', {
                count: ticketCount,
                suffix: isOrgAdmin
                  ? t('closeEventAction.outstandingDialog.suffixOrgAdmin')
                  : t('closeEventAction.outstandingDialog.suffixOther'),
              })}
            </DialogDescription>
          </DialogHeader>

          {ticketCount > 0 && (
            <ul className="max-h-40 space-y-1 overflow-y-auto rounded-sm border border-jq-sand/70 bg-jq-surface-2/50 p-3 font-mono text-[12px] text-jq-charcoal">
              {blocked?.unsettledTicketIds.map((id) => (
                <li key={id}>{id}</li>
              ))}
            </ul>
          )}

          {!isOrgAdmin && (
            <p className="text-[13px] text-jq-graphite">
              {t('closeEventAction.outstandingDialog.nonOrgAdminNote')}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" size="sm" onClick={() => setBlocked(null)}>
              {t('common:actions.cancel')}
            </Button>
            {isOrgAdmin && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => void handleForceClose()}
                disabled={forceClosing}
              >
                {forceClosing
                  ? t('closeEventAction.outstandingDialog.forceClosing')
                  : t('closeEventAction.outstandingDialog.forceClose')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
